/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Step 5: Updating downstream applications and destinations", description = "How to update the applications that consume your event data so each one reads from Hightouch Events after the migration.", lastUpdatedDate = new Date(1782259200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Step 5: Updating downstream applications and destinations",
        description: "How to update the applications that consume your event data so each one reads from Hightouch Events after the migration.",
        lastUpdatedDate: "Wed Jun 24 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Re-check your inventory of downstream applications",
          "depth": 0,
          "id": "re-check-your-inventory-of-downstream-applications"
        }, {
          "value": "Update data sources",
          "depth": 0,
          "id": "update-data-sources"
        }, {
          "value": "Adapt to schema changes",
          "depth": 0,
          "id": "adapt-to-schema-changes"
        }, {
          "value": "Validate metrics and reports",
          "depth": 0,
          "id": "validate-metrics-and-reports"
        }, {
          "value": "Handle data discrepancies",
          "depth": 0,
          "id": "handle-data-discrepancies"
        }, {
          "value": "Manage the transition period",
          "depth": 0,
          "id": "manage-the-transition-period"
        }, {
          "value": "Gather feedback from users",
          "depth": 0,
          "id": "gather-feedback-from-users"
        }, {
          "value": "Turn off your previous provider",
          "depth": 0,
          "id": "turn-off-your-previous-provider"
        }, {
          "value": "What's next",
          "depth": 0,
          "id": "whats-next"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      ul: "ul",
      li: "li",
      ol: "ol",
      strong: "strong"
    }, _provideComponents(), props.components), {StreamingCutoverWarning} = _components;
    if (!StreamingCutoverWarning) _missingMdxReference("StreamingCutoverWarning", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "After validating and unifying your data, update the tools that consume event data so each one reads from Hightouch Events or your unified event tables. This is the final step of the migration."
      }), "\n", _jsx(_components.p, {
        children: "It is possible to run this step in parallel with earlier validation to shorten the migration, but you may need to repeat some steps depending on any changes made during validation or unioning with historical data."
      }), "\n", _jsxs(_components.p, {
        children: ["Before you cut over, make sure ", _jsx(_components.a, {
          href: "/events/monitoring",
          children: "monitoring and alerts"
        }), " are in place so you catch volume drops, contract violations, or sync errors during the transition."]
      }), "\n", _jsx(StreamingCutoverWarning, {}), "\n", _jsx(_components.h2, {
        id: "re-check-your-inventory-of-downstream-applications",
        children: "Re-check your inventory of downstream applications"
      }), "\n", _jsx(_components.p, {
        children: "In your planning, you likely created an initial list of applications consuming event data. Take this opportunity to review and update that list in light of the migration so far."
      }), "\n", _jsx(_components.p, {
        children: "Make sure your list includes any systems, tools, and processes that consume your event data, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Analytics platforms (for example, Google Analytics, Amplitude)"
        }), "\n", _jsx(_components.li, {
          children: "Marketing and advertising tools (for example, Meta Ads, Google Ads)"
        }), "\n", _jsxs(_components.li, {
          children: ["Customer engagement platforms (for example, Braze, ", _jsx(_components.a, {
            href: "http://customer.io/",
            children: "Customer.io"
          }), ")"]
        }), "\n", _jsx(_components.li, {
          children: "Business intelligence tools (for example, Tableau, Looker)"
        }), "\n", _jsx(_components.li, {
          children: "Custom internal dashboards and applications"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Rank your downstream systems based on their criticality to your business operations to plan a phased approach to the updates if necessary."
      }), "\n", _jsx(_components.h2, {
        id: "update-data-sources",
        children: "Update data sources"
      }), "\n", _jsx(_components.p, {
        children: "For each downstream system:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Identify current data source"
          }), ": Determine how the system is currently receiving event data (for example, reverse ETL, streaming, or direct SDKs)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update configurations or set up new syncs"
          }), ": Depending on the downstream tool, you might:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Create new or update existing Hightouch syncs"
            }), "\n", _jsx(_components.li, {
              children: "Set up or modify streaming destinations in Hightouch"
            }), "\n", _jsx(_components.li, {
              children: "Modify configurations in the downstream tool to point to the unified events data."
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Verify data flow"
          }), ": Confirm that events are flowing correctly into the updated system."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "adapt-to-schema-changes",
        children: "Adapt to schema changes"
      }), "\n", _jsx(_components.p, {
        children: "If your migration to Hightouch Events involved changes to your event schema:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Document changes"
          }), ": Create a clear mapping between your old and new event schemas."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update data models"
          }), ": Modify any data models or transformations in your downstream systems to align with the new schema."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Revise queries"
          }), ": Update SQL queries, API calls, or other data retrieval methods to reflect the new event structure."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "validate-metrics-and-reports",
        children: "Validate metrics and reports"
      }), "\n", _jsx(_components.p, {
        children: "For each updated system:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Reconcile key metrics"
          }), ": Compare critical metrics between the old and new data sources to ensure consistency."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update dashboards"
          }), ": Revise any dashboards or standard reports to use the new data source and schema."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cross-check historical data"
          }), ": Verify that historical data is accurately represented in updated reports."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "handle-data-discrepancies",
        children: "Handle data discrepancies"
      }), "\n", _jsx(_components.p, {
        children: "If you encounter differences between your old and new systems:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Investigate root causes"
          }), ": Determine if differences are due to improved data collection, schema changes, or potential issues."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Document explanations"
          }), ": For any persistent differences, document the reasons and communicate these to stakeholders."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Adjust baselines"
          }), ": If necessary, establish new baselines for key metrics based on the new data from Hightouch Events."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "manage-the-transition-period",
        children: "Manage the transition period"
      }), "\n", _jsx(_components.p, {
        children: "If you're executing your migration in a linear fashion, you'll carry out this work as a whole after validating and confirming your new event data and cutting off your prior event collection tool."
      }), "\n", _jsx(_components.p, {
        children: "However, if you're gradually shifting traffic over to Hightouch Events, you might decide to start transitioning downstream applications to use the new data at the same time and run in parallel with your prior tool and historical. If you do so, have a clear date planned for switching all downstream applications over to Hightouch events data and communicate changes to stakeholders as you transition."
      }), "\n", _jsx(_components.h2, {
        id: "gather-feedback-from-users",
        children: "Gather feedback from users"
      }), "\n", _jsx(_components.p, {
        children: "As teams start working with the new event data, gather their feedback and refine your collection and activation setup. Early questions often surface schema or mapping gaps that are easier to fix before you turn off your previous provider."
      }), "\n", _jsx(_components.h2, {
        id: "turn-off-your-previous-provider",
        children: "Turn off your previous provider"
      }), "\n", _jsx(_components.p, {
        children: "Once you're confident in your Hightouch Events setup, you can begin sunsetting your previous provider. We recommend the process below."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Gradual cutover"
          }), ": Incrementally increase the proportion of traffic sent only to Hightouch Events."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Monitor closely"
          }), ": Watch for any unexpected changes in data patterns or downstream system behavior."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Final switch"
          }), ": When ready, update your tracking code to send events only to Hightouch Events. With a wrapper function, this is a one-line change."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Decommission old system"
          }), ": Follow your previous provider's instructions for account closure and data handling."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This step completes your migration. Review your event data flow regularly afterward to keep data quality and downstream consumers in good shape as your tracking plan changes."
      }), "\n", _jsx(_components.h2, {
        id: "whats-next",
        children: "What's next"
      }), "\n", _jsx(_components.p, {
        children: "Your collection now runs on Hightouch Events. Keep these running to maintain data quality and activate your events:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/events/contracts/management",
              children: "Data contracts"
            })
          }), " — enforce your event schema and catch violations as your tracking plan evolves."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/events/monitoring",
              children: "Monitor events"
            })
          }), " — keep volume, violation, and sync alerts running."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/events/troubleshooting",
              children: "Troubleshoot events"
            })
          }), " — diagnose missing events, blocked events, or unexpected warehouse columns."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/customer-studio/overview",
              children: "Build audiences in Customer Studio"
            })
          }), " — activate your event data downstream."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For the complete set of building blocks that make up an Events implementation, see ", _jsx(_components.a, {
          href: "/events/implementation-plan#build-your-implementation",
          children: "Plan your implementation"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
